import React, { createContext, useContext, ReactNode, useCallback, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useChat } from '@/hooks/use-chat';
import { useAppDispatch, useAppSelector } from '@/stores/store';
import { addMessage, addOrUpdateMessage, clearError as clearErrorAction } from '@/stores/slices/chat-slice';
import {
  selectMessages,
  selectStreamingStatus,
  selectCurrentThreadId,
  selectLastError,
  selectIntermediateSteps,
} from '@/stores/selectors/chat-selectors';
import { useAuthContext } from '@/providers/auth-provider';
import { chatAssistantService } from '@/services/chat-assistant-service';
import { getLangGraphClient, langGraphClient as defaultLangGraphClient } from '@/lib/langgraph-client-v2';
import { selectSelectedAgent } from '@/stores/selectors/agent-selectors';
import type { StreamingStatus, IntermediateStep, LangGraphMessageInput, ContentBlock } from '@/types/langgraph';

/**
 * Validate that tool messages have required fields
 * Tool messages must include tool_call_id and name to match with tool_use blocks
 */
export function validateToolMessage(msg: Message): boolean {
  if (msg.type !== 'tool') return true;
  return !!msg.tool_call_id && !!msg.name;
}

/**
 * Convert StreamProvider Message format to LangGraph API format
 * Handles human, AI, and tool message types with proper field mapping
 */
export function toLangGraphMessage(msg: Message): LangGraphMessageInput {
  if (msg.type === 'tool') {
    if (!msg.tool_call_id || !msg.name) {
      throw new Error('Tool messages must have tool_call_id and name');
    }
    return {
      type: 'tool',
      content: typeof msg.content === 'string' ? msg.content : '',
      tool_call_id: msg.tool_call_id,
      name: msg.name,
      id: msg.id,
    };
  }

  return {
    type: msg.type === 'human' ? 'human' : 'ai',
    content: msg.content,
    id: msg.id,
  };
}

/**
 * Tool call format for extracting tool invocation data
 */
export interface ToolCall {
  id: string;
  name: string;
  args?: Record<string, any>;
}

/**
 * Message format for LangGraph-compatible interface
 * Content supports both plain string and multimodal content blocks (text + images)
 */
export interface Message {
  id?: string;
  type: 'human' | 'tool' | 'ai';
  content: string | ContentBlock[];
  tool_call_id?: string;
  name?: string;
  status?: string;
}

/**
 * Submit payload interface
 */
export interface SubmitPayload {
  messages: Message | Message[];
}

/**
 * Stream context interface
 * Provides LangGraph-compatible API bridging to Redux state
 */
export interface StreamContextType {
  // Message submission
  submit: (payload: SubmitPayload) => Promise<void>;

  // Message access (adapted from Redux)
  messages: Message[];

  // Thread management
  currentThreadId: string | null;
  createThread: (userId: string, metadata?: Record<string, any>) => Promise<string | null>;

  // Streaming status
  isStreaming: boolean;
  streamingStatus: StreamingStatus;

  // Intermediate steps (tool calls/results)
  intermediateSteps: IntermediateStep[];
  getLastToolMessage: () => IntermediateStep | null;
  getLastToolCall: () => ToolCall | null;

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

const StreamContext = createContext<StreamContextType | undefined>(undefined);

/**
 * Stream Provider Component
 * Bridges Redux-based chat system to LangGraph-compatible interface
 */
export const StreamProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const dispatch = useAppDispatch();
  const { user } = useAuthContext();
  const { sendMessage } = useChat();
  const selectedAgent = useAppSelector(selectSelectedAgent);

  // Redux state selectors
  const reduxMessages = useAppSelector(selectMessages);
  const streamingStatus = useAppSelector(selectStreamingStatus);
  const currentThreadId = useAppSelector(selectCurrentThreadId);
  const lastError = useAppSelector(selectLastError);
  const intermediateSteps = useAppSelector(selectIntermediateSteps);

  /**
   * Convert Redux ChatMessage format to LangGraph Message format
   */
  const messages = useMemo<Message[]>(() => {
    return reduxMessages.map((msg) => ({
      id: msg.id,
      type: msg.role === 'user' ? 'human' : 'ai',
      content: msg.content,
    }));
  }, [reduxMessages]);

  /**
   * Derived streaming state
   */
  const isStreaming = useMemo(() => {
    return streamingStatus === 'streaming';
  }, [streamingStatus]);

  /**
   * Submit messages to the chat system
   * Supports human, tool, and AI messages with backend synchronization
   */
  const submit = useCallback(
    async (payload: SubmitPayload) => {
      console.log('🔵 [STREAM-PROVIDER] submit() called with payload:', payload);

      const messagesToSubmit = Array.isArray(payload.messages)
        ? payload.messages
        : [payload.messages];

      console.log('🔵 [STREAM-PROVIDER] messagesToSubmit:', messagesToSubmit);

      if (!user?.id) {
        console.error('❌ [STREAM-PROVIDER] Cannot send message: user ID not available');
        return;
      }

      console.log('✅ [STREAM-PROVIDER] User ID available:', user.id);

      // Validate all tool messages have required fields
      for (const msg of messagesToSubmit) {
        if (msg.type === 'tool') {
          console.log('🔍 [STREAM-PROVIDER] Validating tool message:', msg);
          console.log('🔍 [STREAM-PROVIDER] msg.tool_call_id:', msg.tool_call_id);
          console.log('🔍 [STREAM-PROVIDER] msg.name:', msg.name);
          console.log('🔍 [STREAM-PROVIDER] msg keys:', Object.keys(msg));
          const isValid = validateToolMessage(msg);
          console.log('🔍 [STREAM-PROVIDER] Validation result:', isValid);

          if (!isValid) {
            console.error('❌ [STREAM-PROVIDER] Tool message missing required fields:', msg);
            console.error('❌ [STREAM-PROVIDER] tool_call_id:', msg.tool_call_id);
            console.error('❌ [STREAM-PROVIDER] name:', msg.name);
            throw new Error('Tool messages must have tool_call_id and name');
          }
        }
      }

      console.log('✅ [STREAM-PROVIDER] All validations passed');

      // Check if we have any messages that need to be sent to backend
      const needsBackendSubmission = messagesToSubmit.some(
        (msg) => msg.type === 'human' || msg.type === 'tool'
      );

      console.log('🔍 [STREAM-PROVIDER] needsBackendSubmission:', needsBackendSubmission);

      if (needsBackendSubmission) {
        // Convert and send messages (human + tool) to backend
        const langGraphMessages = messagesToSubmit
          .filter((msg) => msg.type === 'human' || msg.type === 'tool')
          .map(toLangGraphMessage);

        console.log('📤 [STREAM-PROVIDER] Sending to backend:', langGraphMessages);
        console.log('📤 [STREAM-PROVIDER] LangGraph messages JSON:', JSON.stringify(langGraphMessages, null, 2));

        try {
          await sendMessage(langGraphMessages, user.id);
          console.log('✅ [STREAM-PROVIDER] sendMessage() completed');
        } catch (sendError) {
          console.error('❌ [STREAM-PROVIDER] sendMessage() error:', sendError);
          throw sendError;
        }
      }

      // Handle AI messages separately (UI only, not sent to backend)
      const aiMessages = messagesToSubmit.filter((msg) => msg.type === 'ai');
      for (const message of aiMessages) {
        if (!message.id) {
          console.warn('⚠️ AI message missing ID, skipping:', message);
          continue;
        }

        dispatch(
          addOrUpdateMessage({
            id: message.id,  // No fallback, ID required
            content: message.content,
            role: 'assistant',
          })
        );
      }
    },
    [dispatch, sendMessage, user?.id]
  );

  /**
   * Create a new thread
   */
  const createThread = useCallback(
    async (userId: string, metadata?: Record<string, any>): Promise<string | null> => {
      const client = selectedAgent
        ? getLangGraphClient(selectedAgent.deploymentUrl, selectedAgent.id)
        : defaultLangGraphClient;
      const [error, thread] = await chatAssistantService.createThread(userId, metadata, client);
      if (error || !thread) {
        console.error('Failed to create thread:', error);
        return null;
      }
      return thread.langGraphThreadId;
    },
    [selectedAgent]
  );

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    dispatch(clearErrorAction());
  }, [dispatch]);

  /**
   * Get the last tool message (tool call or tool result)
   * Returns null if no tool messages exist
   */
  const getLastToolMessage = useCallback((): IntermediateStep | null => {
    if (intermediateSteps.length === 0) {
      return null;
    }

    // Find the last tool_call or tool_result
    for (let i = intermediateSteps.length - 1; i >= 0; i--) {
      const step = intermediateSteps[i];
      if (step.type === 'tool_call' || step.type === 'tool_result') {
        return step;
      }
    }

    return null;
  }, [intermediateSteps]);

  /**
   * Get the last tool call in the format expected by tool components
   * Returns null if no tool call exists
   */
  const getLastToolCall = useCallback((): ToolCall | null => {
    if (intermediateSteps.length === 0) {
      return null;
    }

    // Find the last tool_call
    for (let i = intermediateSteps.length - 1; i >= 0; i--) {
      const step = intermediateSteps[i];
      if (step.type === 'tool_call') {
        // Strip 'tool-' prefix from ID if present (added by LangGraph client)
        // AWS Bedrock expects the raw tool call ID without prefix
        const toolCallId = step.id.startsWith('tool-')
          ? step.id.substring(5)
          : step.id;

        return {
          id: toolCallId,
          name: step.toolName || '',
          args: step.toolInput,
        };
      }
    }

    return null;
  }, [intermediateSteps]);

  const contextValue: StreamContextType = useMemo(
    () => ({
      submit,
      messages,
      currentThreadId,
      createThread,
      isStreaming,
      streamingStatus,
      intermediateSteps,
      getLastToolMessage,
      getLastToolCall,
      lastError,
      clearError,
    }),
    [
      submit,
      messages,
      currentThreadId,
      createThread,
      isStreaming,
      streamingStatus,
      intermediateSteps,
      getLastToolMessage,
      getLastToolCall,
      lastError,
      clearError,
    ]
  );

  return <StreamContext.Provider value={contextValue}>{children}</StreamContext.Provider>;
};

/**
 * Hook to access stream context
 * @throws Error if used outside of StreamProvider
 */
export const useStreamContext = (): StreamContextType => {
  const context = useContext(StreamContext);
  if (context === undefined) {
    throw new Error('useStreamContext must be used within a StreamProvider');
  }
  return context;
};

export default StreamContext;
